export type StoreDisabledTime = {
  id: string;
  storeName: string;
  mode: "daily" | "weeklyDay" | "weeklyTime" | "date" | "once";
  weekday?: number;
  date?: string;
  startTime?: string;
  endTime?: string;
  startAt?: string;
  endAt?: string;
};

const minutes = (time: string) => Number(time.slice(0, 2)) * 60 + Number(time.slice(3, 5));

// Date and clock strings are deliberately compared as wall time. These rules
// only paint the calendar; they never convert or rewrite a schedule timestamp.
export function disabledRangesForDay(rule: StoreDisabledTime, date: string, weekday: number): Array<[number, number]> {
  if (rule.mode === "daily" || rule.mode === "weeklyTime") {
    const start = minutes(rule.startTime!);
    const end = minutes(rule.endTime!);
    const appliesToday = rule.mode === "daily" || rule.weekday === weekday;
    const carriesFromYesterday = rule.mode === "daily" || rule.weekday === (weekday + 6) % 7;
    if (start < end) return appliesToday ? [[start, end]] : [];
    const ranges: Array<[number, number]> = [];
    if (carriesFromYesterday) ranges.push([0, end]);
    if (appliesToday) ranges.push([start, 1440]);
    return ranges;
  }
  if ((rule.mode === "weeklyDay" && rule.weekday === weekday) || (rule.mode === "date" && rule.date === date)) return [[0, 1440]];
  if (rule.mode !== "once" || !rule.startAt || !rule.endAt) return [];
  const dayStart = `${date}T00:00`;
  const dayEnd = `${date}T23:59`;
  if (rule.endAt <= dayStart || rule.startAt > dayEnd) return [];
  return [[rule.startAt.slice(0, 10) === date ? minutes(rule.startAt.slice(11)) : 0,
    rule.endAt.slice(0, 10) === date ? minutes(rule.endAt.slice(11)) : 1440]];
}

export function slotPositionForMinute(slots: Array<{ minute: number; top: number; bottom: number }>, minute: number): number {
  if (!slots.length) return 0;
  if (minute <= slots[0].minute) return slots[0].top;
  for (let index = 0; index < slots.length; index++) {
    const slot = slots[index];
    const next = slots[index + 1];
    const endMinute = next?.minute ?? slot.minute + (index > 0 ? slot.minute - slots[index - 1].minute : 30);
    const bottom = next?.top ?? slot.bottom;
    if (minute <= endMinute) {
      return slot.top + (bottom - slot.top) * Math.min(1, (minute - slot.minute) / (endMinute - slot.minute));
    }
  }
  return slots[slots.length - 1].bottom;
}

// A workday column may include the first two hours of the next civil day.
export function disabledRangesForWorkday(rule: StoreDisabledTime, date: string, weekday: number): Array<[number, number]> {
  const next = new Date(`${date}T12:00:00`);
  next.setDate(next.getDate() + 1);
  const nextDate = `${next.getFullYear()}-${String(next.getMonth() + 1).padStart(2, "0")}-${String(next.getDate()).padStart(2, "0")}`;
  const night = disabledRangesForDay(rule, nextDate, (weekday + 1) % 7)
    .map(([start, end]): [number, number] => [1440 + start, 1440 + Math.min(end, 120)])
    .filter(([start, end]) => end > start);
  return [...disabledRangesForDay(rule, date, weekday), ...night];
}
